<template>
  <div>
    <p v-if="displayType === 'long'">
      Trailing volatilities presented in this analysis are hypothetical in nature. They do not reflect actual investment results and are not guarantees of future results.

      Trailing volatilities are presented as of {{ lastWeekDayDate }}. Volatility is based off of historical returns data for each security in the portfolio, adjusted for splits dividends, and reinvestment of dividends and capital gains. Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.

      Volatility coverage includes Equities, Mutual Funds, ETFs, and Cash.
      <template v-if="portfolio && benchmark">
        These volatility calculations cover {{ (portfolio.trailing_volatilities?.[2] * 100).toFixed(2) }}% of {{ portfolio.name }} and {{ (benchmark.trailing_volatilities?.[2] * 100).toFixed(2) }}% of {{ benchmark.name }} overall.
      </template>
      <template v-else-if="portfolio">
        These volatility calculations cover {{ (portfolio.trailing_volatilities?.[2] * 100).toFixed(2) }}% of {{ portfolio.name }} overall.
      </template>
      <template v-else-if="benchmark">
        These volatility calculations cover {{ (benchmark.trailing_volatilities?.[2] * 100).toFixed(2) }}% of {{ benchmark.name }} overall.
      </template>

      Below is the percent of the portfolio that has the full period of returns for each returns period:
      <template v-if="portfolio">
        {{ portfolio.name }}:
        1 year:  {{ (portfolio.trailing_volatilities?.[1]?.trailing_1_yr_volatility * 100).toFixed(2) }}
        3 year:  {{ (portfolio.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}
        5 year:  {{ (portfolio.trailing_volatilities?.[1]?.trailing_5_yr_volatility * 100).toFixed(2) }}
        10 year: {{ (portfolio.trailing_volatilities?.[1]?.trailing_10_yr_volatility * 100).toFixed(2) }}
      </template>

      <template v-if="benchmark">
        {{ benchmark.name }}:
        1 year:  {{ (benchmark.trailing_volatilities?.[1]?.trailing_1_yr_volatility * 100).toFixed(2) }}
        3 year:  {{ (benchmark.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}
        5 year:  {{ (benchmark.trailing_volatilities?.[1]?.trailing_5_yr_volatility * 100).toFixed(2) }}
        10 year: {{ (benchmark.trailing_volatilities?.[1]?.trailing_10_yr_volatility * 100).toFixed(2) }}
      </template>

      Volatilities are calculated by taking the standard deviation of daily portfolio returns. Daily portfolio returns are calculated by multiplying the returns of each covered security in the portfolio by that security's current allocation in the portfolio using the following formula:
      Rp,n = Sum(Wi,n x Ri,n) from i=1 to m, where Rp,n is the portfolio returns at month n, Wi,n is the weight of asset i in month n, Ri,n is the monthly return of asset i in month n, and there are m securities with returns in month n. We assume Cash has 0 volatility for all monthly periods.

      When daily returns are not available for a holding (e.g. the security did not exist during historical period being reported), the remaining portfolio holdings are re-weighted to maintain consistent ratios using the following formula: Wnew,i,n = Wcurrent,i x (SUMcovered(Wcurrent,i)) / (1-(SUMnot_covered(Wcurrent,i))), where Wnew,i,n is the adjusted weight of security i at day n, SUMnot_covered is the sum over all securities that do not have daily returns available at day n, SUMcovered is the sum over all the securities that do have daily returns available at day n, and Wcurrent is the current weight of security i.

      Trailing volatilities are annualized by multiplying the standard deviation of daily portfolio returns by the square root of 252.
    </p>

    <p v-else>
      Trailing volatilities are presented as of {{ lastWeekDayDate }}. Volatility is based off of historical returns data for each security in the portfolio, adjusted for splits dividends, and reinvestment of dividends and capital gains. Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees. Volatility coverage includes Equities, Mutual Funds, ETFs, and Cash.
      <template v-if="portfolio && benchmark">
        These volatility calculations cover {{ (portfolio.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}% of {{ portfolio.name }} and {{ (benchmark.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}% of {{ benchmark.name }} overall.
      </template>
      <template v-else-if="portfolio">
        These volatility calculations cover {{ (portfolio.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}% of {{ portfolio.name }} overall.
      </template>
      <template v-else-if="benchmark">
        These volatility calculations cover {{ (benchmark.trailing_volatilities?.[1]?.trailing_3_yr_volatility * 100).toFixed(2) }}% of {{ benchmark.name }} overall.
      </template>
      See full methodology below.
    </p>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
export default {
  name: 'VolatilityMethodology',
  props: ['displayType', 'portfolio', 'benchmark'],
  computed: {
    ...mapStores(useCheckupStore),
    lastWeekDayDate() {
      let date = new Date();
      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) { // Ensure it's a valid date
          date = createdDate
        }
      }
      const lastWeekDay = date.getDay() === 0 ? 6 : date.getDay() - 1;
      const lastWeekDayDate = new Date(date.setDate(date.getDate() - lastWeekDay));
      return lastWeekDayDate.toLocaleDateString();
    },
  },


};
</script>
